"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar as CalendarIcon, Users, MessageSquare, Phone, Mail, User, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ScheduleTourForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 py-12 text-center">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          <SafeHydrate><CalendarIcon className="size-8" /></SafeHydrate>
        </div>
        <h3 className="font-serif text-2xl font-semibold text-foreground">Tour Requested</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Schedule Another Visit
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Jane Doe" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormDescription>Please enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="jane@example.com" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[0-9\-\+\s\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="(555) 123-4567" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Preferred Visit Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Visit Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" className="pl-3" {...field} />
                </div>
              </FormControl>
              <FormDescription>Select a date that works best for you.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="visitors" rules={{
      required: "Number of Visitors is required",
      min: {
        value: 1,
        message: "Minimum 1 visitor"
      },
      max: {
        value: 5,
        message: "Maximum 5 visitors"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Number of Visitors</FormLabel>
            <FormControl>
              <div className="relative">
                <Users className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input type="number" min="1" max="5" placeholder="2" className="pl-9" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="comments" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Comments / Special Needs</FormLabel>
            <FormControl>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 size-4 text-muted-foreground" />
                <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 pl-9 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50" placeholder="Any special accommodations or questions?" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Scheduling...
          </> : "Schedule Private Visit"}
      </Button>
    </Form>;
}
export default function VisitOurHome() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="visit-our-home" className="bg-background py-16 md:py-24" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={`grid gap-12 lg:grid-cols-2 lg:gap-16 items-start transition-all duration-1000 ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative h-full min-h-[400px] lg:min-h-[600px] overflow-hidden rounded-3xl shadow-xl">
            <img src="https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/Front_side_silver_spring.png" alt="Welcoming front entrance of our boutique residential assisted living home in Silver Spring, Maryland" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3">
                Your Boutique Residential Home in Silver Spring
              </h2>
              <p className="text-white/90 max-w-md text-lg">
                Experience the intimate, family-like atmosphere of our Silver Spring, Maryland home. With a capacity for only 5 residents, we offer truly personalized care in a warm, boutique residential setting.
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <Card className="border-none shadow-2xl bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-8">
                <CardTitle className="font-serif text-3xl text-foreground">
                  Schedule Your Private Tour
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  Join us for a private visit to see our small-scale residential home and experience our family-like environment firsthand. Limited to 5 residents for maximum care.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="visit_home_form_schedule_a_tour" sectionName="VisitOurHome" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                preferred_date: "",
                visitors: 2,
                comments: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                preferred_date: {
                  required: true
                },
                visitors: {
                  required: true,
                  min: 1,
                  max: 5
                }
              }} submitText="Schedule Private Visit" successMessage="Thank you for your interest! We've received your request and will contact you shortly to confirm your private tour of our small residential home and personalized care environment.">
                  <ScheduleTourForm />
                </FormProvider>
              </CardContent>
            </Card>
            
            <div className="mt-8 space-y-6 px-4">
              <div className="space-y-3">
                <h4 className="font-serif text-xl font-bold text-foreground">Family First Senior Care</h4>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="size-4 mt-0.5 shrink-0" />
                  <span>3914 Ilford Rd, Silver Spring, MD 20906</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Phone className="size-4 shrink-0" />
                    <span>301-477-9779</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="size-4 shrink-0" />
                    <span>info@familyfirstassistedliving.org</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed">
                We encourage families to call us directly at 301-477-9779 or schedule a tour using the form above. We look forward to welcoming you to our home and discussing how we can support your family's needs.
              </p>

              <div className="w-full h-64 rounded-2xl overflow-hidden border border-border shadow-sm">
                <iframe title="Google Map of Family First Senior Care" src="https://www.google.com/maps?q=3914+Ilford+Rd,+Silver+Spring,+MD+20906&output=embed" className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}